/**
 * The User Guide manifest: every help page, who may see it, and where it sits in the Help menu.
 *
 * This is the single source for the help routes (`router/helpRoutes.ts`), the route guard
 * (`router/permissionGuard.ts`), the Help menu (`components/menu/HelpMenu.vue`) and the help
 * search (`composables/useHelpSearch.ts`), so the four can never disagree about which pages a
 * user can see.
 */

/** Reads a setting the way `useSettings().getSetting` does. */
export type HelpSettingReader = (sid: keyof AppSettings) => unknown;

/** The guide a page belongs to. Search results show it as their subtitle. */
export type HelpGuide = 'publisher' | 'admin' | 'maintenance' | 'glossary';

/** A translated title (with an optional plural count), or a product name that is never translated. */
export type HelpTitle = { key: string; count?: number } | { text: string };

export interface HelpPage {
  /** Route path, e.g. `/docs/admin/ministry-management/events`. */
  path: string;
  /** Unique route name. */
  name: string;
  /** Page component, relative to `resources/js/pages/help/`. */
  file: string;
  guide: HelpGuide;
  title: HelpTitle | ((getSetting: HelpSettingReader) => HelpTitle);
  /** The user needs at least one of these permissions. */
  permissions?: string[];
  /** Only administrators (users with any admin permission) may see the page. */
  adminOnly?: boolean;
  /** Extra rule on application settings. */
  isVisible?: (getSetting: HelpSettingReader) => boolean;
}

export interface HelpMenuItem {
  path: string;
  /** Permissions needed for this menu entry on top of the page's own rule. */
  permissions?: string[];
  /** Title for this menu entry when it differs from the page's own title. */
  title?: HelpTitle;
  /**
   * Links to `path#` instead of `path`. Used where the same page is listed in two menu groups,
   * so that only the entry that was clicked shows as active.
   */
  appendHash?: boolean;
}

export interface HelpMenuSection {
  titleKey: string;
  items: HelpMenuItem[];
}

export interface HelpMenuGroup {
  value: HelpGuide;
  title: HelpTitle;
  icon: string;
  sections: HelpMenuSection[];
}

const isLocationOverseerMode = (getSetting: HelpSettingReader): boolean => getSetting('day_overseer_display_type') === 'location';

export const helpPages: HelpPage[] = [
  // Publisher guide
  {
    path: '/docs/publisher',
    name: 'Docs - Publishers',
    file: 'publisher/DocumentationPublisherWelcome.vue',
    guide: 'publisher',
    title: { key: 'global.welcome' },
  },
  {
    path: '/docs/publisher/starting/my-details',
    name: 'Docs - Publishers - My Details',
    file: 'publisher/starting/DocumentationPublisherMyDetails.vue',
    guide: 'publisher',
    title: { key: 'global.my_details' },
  },
  {
    path: '/docs/publisher/before-shifts/news',
    name: 'Docs - Publishers - News',
    file: 'publisher/before-shifts/DocumentationPublisherNews.vue',
    guide: 'publisher',
    title: { key: 'news.title' },
    permissions: ['access_news_page'],
  },
  {
    path: '/docs/publisher/before-shifts/my-tasks',
    name: 'Docs - Publishers - My Tasks',
    file: 'publisher/before-shifts/DocumentationPublisherMyTasks.vue',
    guide: 'publisher',
    title: { key: 'task.mine' },
    permissions: ['handle_tasks', 'manage_tasks'],
  },
  {
    path: '/docs/publisher/before-shifts/current-carts',
    name: 'Docs - Publishers - Current Carts',
    file: 'publisher/before-shifts/DocumentationPublisherCurrentCarts.vue',
    guide: 'publisher',
    title: { key: 'cart.current' },
    permissions: ['access_cart_page'],
  },
  {
    path: '/docs/publisher/before-shifts/day-overseers',
    name: 'Docs - Publishers - Day Overseers',
    file: 'publisher/before-shifts/DocumentationPublisherDayOverseers.vue',
    guide: 'publisher',
    title: { key: 'dayoverseer.title', count: 2 },
    permissions: ['access_dayoverseers_page'],
    isVisible: getSetting => !isLocationOverseerMode(getSetting),
  },
  {
    path: '/docs/publisher/before-shifts/location-overseers',
    name: 'Docs - Publishers - Location Overseers',
    file: 'publisher/before-shifts/DocumentationPublisherLocationOverseers.vue',
    guide: 'publisher',
    title: { key: 'dayoverseer.location_overseer', count: 2 },
    permissions: ['access_dayoverseers_page'],
    isVisible: isLocationOverseerMode,
  },
  {
    path: '/docs/publisher/managing-shifts/availability',
    name: 'Docs - Publishers - Availability',
    file: 'publisher/managing-shifts/DocumentationPublisherSubmitAvailability.vue',
    guide: 'publisher',
    title: { key: 'smpw.submit_availability' },
    permissions: ['submit_availability'],
  },
  {
    path: '/docs/publisher/managing-shifts/my-shifts',
    name: 'Docs - Publishers - My Shifts',
    file: 'publisher/managing-shifts/DocumentationPublisherMyShifts.vue',
    guide: 'publisher',
    title: { key: 'shift.mine' },
  },
  {
    path: '/docs/publisher/managing-shifts/calendar',
    name: 'Docs - Publishers - Calendar',
    file: 'publisher/managing-shifts/DocumentationPublisherMyCalendar.vue',
    guide: 'publisher',
    title: { key: 'calendar.mine' },
  },
  {
    path: '/docs/publisher/managing-shifts/current-needs',
    name: 'Docs - Publishers - Current Needs',
    file: 'publisher/managing-shifts/DocumentationPublisherCurrentNeeds.vue',
    guide: 'publisher',
    title: { key: 'shift.current_needs' },
  },
  {
    path: '/docs/publisher/managing-shifts/locations',
    name: 'Docs - Publishers - Locations',
    file: 'publisher/managing-shifts/DocumentationPublisherLocations.vue',
    guide: 'publisher',
    title: { key: 'location.title', count: 2 },
    permissions: ['access_locations_page'],
  },
  {
    path: '/docs/publisher/after-shifts/experiences',
    name: 'Docs - Publishers - Experiences',
    file: 'publisher/after-shifts/DocumentationPublisherExperiences.vue',
    guide: 'publisher',
    title: { key: 'experience.title', count: 2 },
    permissions: ['access_experiences_page'],
  },
  {
    path: '/docs/publisher/after-shifts/contact-us',
    name: 'Docs - Publishers - Contact Us',
    file: 'publisher/after-shifts/DocumentationPublisherContactUs.vue',
    guide: 'publisher',
    title: { key: 'actions.contact_us' },
  },

  // Administrator guide
  {
    path: '/docs/admin',
    name: 'Docs - Admin',
    file: 'admin/DocumentationAdminWelcome.vue',
    guide: 'admin',
    title: { key: 'global.welcome' },
    adminOnly: true,
  },
  {
    path: '/docs/admin/initial-setup',
    name: 'Docs - Admin - Initial Setup',
    file: 'admin/DocumentationAdminInitialSetup.vue',
    guide: 'admin',
    title: { key: 'global.initial_setup' },
    adminOnly: true,
  },
  {
    path: '/docs/admin/ministry-management/schedule',
    name: 'Docs - Admin - Schedule',
    file: 'admin/ministry-management/DocumentationAdminSchedule.vue',
    guide: 'admin',
    title: { key: 'admin_menu.schedule' },
    adminOnly: true,
    permissions: ['manage_shifts', 'view_all_shifts'],
  },
  {
    path: '/docs/admin/ministry-management/currentneeds',
    name: 'Docs - Admin - Current Needs',
    file: 'admin/ministry-management/DocumentationAdminCurrentNeeds.vue',
    guide: 'admin',
    title: { key: 'shift.current_needs' },
    adminOnly: true,
  },
  {
    path: '/docs/admin/ministry-management/experiences',
    name: 'Docs - Admin - Experiences',
    file: 'admin/ministry-management/DocumentationAdminExperiences.vue',
    guide: 'admin',
    title: { key: 'experience.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_experiences'],
  },
  {
    path: '/docs/admin/ministry-management/day-overseers',
    name: 'Docs - Admin - Day Overseers',
    file: 'admin/ministry-management/DocumentationAdminDayLocationOverseer.vue',
    guide: 'admin',
    title: getSetting => (isLocationOverseerMode(getSetting)
      ? { key: 'dayoverseer.location_overseer', count: 2 }
      : { key: 'dayoverseer.title', count: 2 }),
    adminOnly: true,
    permissions: ['manage_dayoverseers'],
  },
  {
    path: '/docs/admin/ministry-management/shift-plan',
    name: 'Docs - Admin - Shift Plan',
    file: 'admin/ministry-management/DocumentationAdminShiftPlan.vue',
    guide: 'admin',
    title: { key: 'shift_plan.title' },
    adminOnly: true,
    permissions: ['manage_shift_plans'],
  },
  {
    path: '/docs/admin/ministry-management/events',
    name: 'Docs - Admin - Events',
    file: 'admin/ministry-management/DocumentationAdminEvents.vue',
    guide: 'admin',
    title: { key: 'event.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_events'],
  },
  {
    path: '/docs/admin/ministry-management/shift-tags',
    name: 'Docs - Admin - Shift Tags',
    file: 'admin/ministry-management/DocumentationAdminShiftTags.vue',
    guide: 'admin',
    title: { key: 'shift_tag.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_shift_tags'],
  },
  {
    path: '/docs/admin/ministry-management/locations',
    name: 'Docs - Admin - Locations',
    file: 'admin/ministry-management/DocumentationAdminLocations.vue',
    guide: 'admin',
    title: { key: 'location.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_locations'],
  },
  {
    path: '/docs/admin/ministry-management/stores',
    name: 'Docs - Admin - Stores',
    file: 'admin/ministry-management/DocumentationAdminStores.vue',
    guide: 'admin',
    title: { key: 'store.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_stores'],
  },
  {
    path: '/docs/admin/ministry-management/publications',
    name: 'Docs - Admin - Publications',
    file: 'admin/ministry-management/DocumentationAdminPublications.vue',
    guide: 'admin',
    title: { key: 'publication.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_publications'],
  },
  {
    path: '/docs/admin/ministry-management/carts',
    name: 'Docs - Admin - Carts',
    file: 'admin/ministry-management/DocumentationAdminCarts.vue',
    guide: 'admin',
    title: { key: 'cart.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_cart'],
  },
  {
    path: '/docs/admin/ministry-management/timebands',
    name: 'Docs - Admin - Time Bands',
    file: 'admin/ministry-management/DocumentationAdminTimeBands.vue',
    guide: 'admin',
    title: { key: 'shift.time_band', count: 2 },
    adminOnly: true,
    permissions: ['manage_time_bands'],
  },
  {
    path: '/docs/admin/publisher-management/publishers',
    name: 'Docs - Admin - Publishers',
    file: 'admin/publisher-management/DocumentationAdminPublishers.vue',
    guide: 'admin',
    title: { key: 'smpw.publisher', count: 2 },
    adminOnly: true,
    permissions: ['manage_users', 'view_user_details', 'manage_circuit_users'],
  },
  {
    path: '/docs/admin/publisher-management/congregations',
    name: 'Docs - Admin - Congregations',
    file: 'admin/publisher-management/DocumentationAdminCongregations.vue',
    guide: 'admin',
    title: { key: 'congregation.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_congregations', 'manage_circuit_congregations'],
  },
  {
    path: '/docs/admin/publisher-management/circuits',
    name: 'Docs - Admin - Circuits',
    file: 'admin/publisher-management/DocumentationAdminCircuits.vue',
    guide: 'admin',
    title: { key: 'circuit.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_circuits'],
  },
  {
    path: '/docs/admin/publisher-management/teams',
    name: 'Docs - Admin - Teams',
    file: 'admin/publisher-management/DocumentationAdminTeams.vue',
    guide: 'admin',
    title: { key: 'team.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_teams'],
  },
  {
    path: '/docs/admin/publisher-management/training',
    name: 'Docs - Admin - Training',
    file: 'admin/publisher-management/DocumentationAdminTraining.vue',
    guide: 'admin',
    title: { key: 'course.training' },
    adminOnly: true,
    permissions: ['manage_courses', 'manage_course_sessions'],
  },
  {
    path: '/docs/admin/publisher-management/bulk-import',
    name: 'Docs - Admin - Bulk Import',
    file: 'admin/publisher-management/DocumentationAdminBulkImport.vue',
    guide: 'admin',
    title: { key: 'user_import.bulk_import' },
    adminOnly: true,
    permissions: ['import_users'],
  },
  {
    path: '/docs/admin/publisher-management/rating-labels',
    name: 'Docs - Admin - Rating Labels',
    file: 'admin/publisher-management/DocumentationAdminRatingLabels.vue',
    guide: 'admin',
    title: { key: 'user_rating_label.short' },
    adminOnly: true,
    permissions: ['manage_user_rating_labels'],
  },
  {
    path: '/docs/admin/site-management/reports',
    name: 'Docs - Admin - Reports',
    file: 'admin/site-management/DocumentationAdminReports.vue',
    guide: 'admin',
    title: { key: 'report.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_reports', 'access_reports'],
  },
  {
    path: '/docs/admin/site-management/news',
    name: 'Docs - Admin - News',
    file: 'admin/site-management/DocumentationAdminNews.vue',
    guide: 'admin',
    title: { key: 'news.title' },
    adminOnly: true,
    permissions: ['manage_news'],
  },
  {
    path: '/docs/admin/site-management/alerts',
    name: 'Docs - Admin - Alerts',
    file: 'admin/site-management/DocumentationAdminAlerts.vue',
    guide: 'admin',
    title: { key: 'alert.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_alert_messages'],
  },
  {
    path: '/docs/admin/site-management/faqs',
    name: 'Docs - Admin - FAQs',
    file: 'admin/site-management/DocumentationAdminFAQs.vue',
    guide: 'admin',
    title: { key: 'faq.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_faqs'],
  },
  {
    path: '/docs/admin/site-management/videos',
    name: 'Docs - Admin - Videos',
    file: 'admin/site-management/DocumentationAdminVideos.vue',
    guide: 'admin',
    title: { key: 'video.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_videos'],
  },
  {
    path: '/docs/admin/site-management/documents',
    name: 'Docs - Admin - Documents',
    file: 'admin/site-management/DocumentationAdminDocuments.vue',
    guide: 'admin',
    title: { key: 'document.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_documents'],
  },
  {
    path: '/docs/admin/site-management/taskcategories',
    name: 'Docs - Admin - Task Categories',
    file: 'admin/site-management/DocumentationAdminTaskCategories.vue',
    guide: 'admin',
    title: { key: 'task.category.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_task_categories'],
  },
  {
    path: '/docs/admin/site-management/contact-options',
    name: 'Docs - Admin - Contact Options',
    file: 'admin/site-management/DocumentationAdminContactOptions.vue',
    guide: 'admin',
    title: { key: 'contact_option.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_contact_message_options'],
  },
  {
    path: '/docs/admin/site-management/cancellation-reasons',
    name: 'Docs - Admin - Cancellation Reasons',
    file: 'admin/site-management/DocumentationAdminCancellationReasons.vue',
    guide: 'admin',
    title: { key: 'cancellation_reason.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_shift_cancellation_reasons'],
  },
  {
    path: '/docs/admin/site-management/tags',
    name: 'Docs - Admin - Tags',
    file: 'admin/site-management/DocumentationAdminTags.vue',
    guide: 'admin',
    title: { key: 'tags.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_tags'],
  },
  {
    path: '/docs/admin/settings/settings',
    name: 'Docs - Admin - Settings',
    file: 'admin/settings/DocumentationAdminSettings.vue',
    guide: 'admin',
    title: { key: 'setting.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_settings'],
  },
  {
    path: '/docs/admin/settings/roles',
    name: 'Docs - Admin - Roles',
    file: 'admin/settings/DocumentationAdminRoles.vue',
    guide: 'admin',
    title: { key: 'role.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_roles', 'assign_roles', 'assign_any_role'],
  },
  {
    path: '/docs/admin/settings/languages',
    name: 'Docs - Admin - Languages',
    file: 'admin/settings/DocumentationAdminLanguages.vue',
    guide: 'admin',
    title: { key: 'language.title', count: 2 },
    adminOnly: true,
    permissions: ['manage_languages'],
  },
  {
    path: '/docs/admin/settings/mcp-server',
    name: 'Docs - Admin - MCP Server',
    file: 'admin/settings/DocumentationAdminMcp.vue',
    guide: 'admin',
    title: { key: 'mcp.title' },
    adminOnly: true,
    permissions: ['manage_mcp'],
  },
  {
    path: '/docs/admin/maintenance/system-info',
    name: 'Docs - Admin - System Information',
    file: 'admin/maintenance/DocumentationAdminSystemInfo.vue',
    guide: 'admin',
    title: { key: 'maintenance.system_info' },
    adminOnly: true,
    permissions: ['access_system_monitor'],
  },
  {
    path: '/docs/admin/maintenance/system-monitor-dashboard',
    name: 'Docs - Admin - System Monitor/Dashboard',
    file: 'admin/maintenance/DocumentationAdminSystemMonitorDashboard.vue',
    guide: 'admin',
    title: { key: 'maintenance.system_monitor_dashboard' },
    adminOnly: true,
    permissions: ['access_system_monitor'],
  },

  // Maintenance guide
  {
    path: '/docs/maintenance',
    name: 'Docs - Maintenance',
    file: 'maintenance/DocumentationMaintenanceWelcome.vue',
    guide: 'maintenance',
    title: { key: 'global.welcome' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/initial-setup',
    name: 'Docs - Maintenance - Initial Setup',
    file: 'maintenance/DocumentationMaintenanceInitialSetup.vue',
    guide: 'maintenance',
    title: { key: 'global.initial_setup' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/obtain-domain',
    name: 'Docs - Maintenance - Obtain a Domain Name',
    file: 'maintenance/installation/DocumentationMaintenanceObtainDomain.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.obtain_domain' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/aws-rds-setup',
    name: 'Docs - Maintenance - AWS RDS Setup',
    file: 'maintenance/installation/DocumentationMaintenanceAWSRDSSetup.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.aws_rds_setup' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/local-database-setup',
    name: 'Docs - Maintenance - Local Database Setup',
    file: 'maintenance/installation/DocumentationMaintenanceLocalDatabaseSetup.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.local_database_setup' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/aws-ec2-setup',
    name: 'Docs - Maintenance - AWS EC2 Setup',
    file: 'maintenance/installation/DocumentationMaintenanceAWSEC2Setup.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.aws_ec2_setup' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/application-install',
    name: 'Docs - Maintenance - Application Install',
    file: 'maintenance/installation/DocumentationMaintenanceApplicationInstall.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.application_install' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/aws-s3-setup',
    name: 'Docs - Maintenance - AWS S3 Setup',
    file: 'maintenance/installation/DocumentationMaintenanceAWSS3Setup.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.aws_s3_setup' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/outbound-email-setup',
    name: 'Docs - Maintenance - Outbound Email Setup',
    file: 'maintenance/installation/DocumentationMaintenanceOutboundEmailSetup.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.outbound_email_setup' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/post-install-configuration',
    name: 'Docs - Maintenance - Post-Install Configuration',
    file: 'maintenance/installation/DocumentationMaintenancePostInstallConfig.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.post_install_configuration' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/aws-lightsail-setup',
    name: 'Docs - Maintenance - AWS Lightsail Setup',
    file: 'maintenance/installation/DocumentationMaintenanceAWSLightsailSetup.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.aws_lightsail_setup' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/ionos-domain-setup',
    name: 'Docs - Maintenance - IONOS Domain Setup',
    file: 'maintenance/installation/DocumentationMaintenanceIonosDomainSetup.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.ionos_domain_setup' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/ionos-database-setup',
    name: 'Docs - Maintenance - IONOS Database Setup',
    file: 'maintenance/installation/DocumentationMaintenanceIonosDatabaseSetup.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.ionos_database_setup' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/ionos-application-install',
    name: 'Docs - Maintenance - IONOS Application Install',
    file: 'maintenance/installation/DocumentationMaintenanceIonosApplicationInstall.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.ionos_application_install' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/google-maps-integration',
    name: 'Docs - Maintenance - Google Maps Integration',
    file: 'maintenance/integration/DocumentationMaintenanceGoogleMaps.vue',
    guide: 'maintenance',
    title: { text: 'Google Maps' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/openweathermap-integration',
    name: 'Docs - Maintenance - OpenWeatherMap Integration',
    file: 'maintenance/integration/DocumentationMaintenanceOpenWeatherMap.vue',
    guide: 'maintenance',
    title: { text: 'OpenWeatherMap' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/google-authentication-setup',
    name: 'Docs - Maintenance - Google Authentication Setup',
    file: 'maintenance/integration/DocumentationMaintenanceGoogleAuth.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.google_auth_setup' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/google-contacts-integration',
    name: 'Docs - Maintenance - Google Contacts Integration',
    file: 'maintenance/integration/DocumentationMaintenanceGoogleContacts.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.google_contacts' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/google-calendar-integration',
    name: 'Docs - Maintenance - Google Calendar Integration',
    file: 'maintenance/integration/DocumentationMaintenanceGoogleCalendar.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.google_calendar' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/google-drive-integration',
    name: 'Docs - Maintenance - Google Drive Integration',
    file: 'maintenance/integration/DocumentationMaintenanceGoogleDrive.vue',
    guide: 'maintenance',
    title: { text: 'Google Drive' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/updating',
    name: 'Docs - Maintenance - Update LSM',
    file: 'maintenance/ongoing/DocumentationMaintenanceUpdating.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.updating_application' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/update-aws-rds',
    name: 'Docs - Maintenance - Update AWS RDS',
    file: 'maintenance/ongoing/DocumentationMaintenanceUpdateAWSRDS.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.update_aws_rds' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/migrate-mysql-to-mariadb',
    name: 'Docs - Maintenance - Migrate MySQL to MariaDB',
    file: 'maintenance/ongoing/DocumentationMaintenanceMigrateMariaDB.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.migrate_mariadb' },
    adminOnly: true,
  },
  {
    path: '/docs/maintenance/background-tasks',
    name: 'Docs - Maintenance - Background Tasks',
    file: 'maintenance/miscellaneous/DocumentationMaintenanceBackgroundTasks.vue',
    guide: 'maintenance',
    title: { key: 'documentation.maintenance.background_tasks' },
    adminOnly: true,
  },

  // Shared
  {
    path: '/docs/glossary',
    name: 'Docs - Glossary',
    file: 'Glossary.vue',
    guide: 'glossary',
    title: { key: 'glossary.title' },
  },
];

/** The guide groups of the Help menu, in display order. The glossary is listed on its own after them. */
export const helpMenu: HelpMenuGroup[] = [
  {
    value: 'publisher',
    title: { key: 'smpw.publisher', count: 2 },
    icon: 'fas fa-users',
    sections: [
      {
        titleKey: 'help.getting_started',
        items: [
          { path: '/docs/publisher' },
          { path: '/docs/publisher/starting/my-details' },
        ],
      },
      {
        titleKey: 'shift.before_shift',
        items: [
          { path: '/docs/publisher/before-shifts/news' },
          { path: '/docs/publisher/before-shifts/my-tasks' },
          { path: '/docs/publisher/before-shifts/current-carts' },
          { path: '/docs/publisher/before-shifts/day-overseers' },
          { path: '/docs/publisher/before-shifts/location-overseers' },
        ],
      },
      {
        titleKey: 'shift.managing',
        items: [
          { path: '/docs/publisher/managing-shifts/availability' },
          { path: '/docs/publisher/managing-shifts/my-shifts' },
          { path: '/docs/publisher/managing-shifts/calendar' },
          { path: '/docs/publisher/managing-shifts/current-needs' },
          { path: '/docs/publisher/managing-shifts/locations' },
        ],
      },
      {
        titleKey: 'shift.after_shift',
        items: [
          { path: '/docs/publisher/after-shifts/experiences' },
          { path: '/docs/publisher/after-shifts/contact-us' },
        ],
      },
    ],
  },
  {
    value: 'admin',
    title: { key: 'global.administration' },
    icon: 'fas fa-clipboard-user',
    sections: [
      {
        titleKey: 'help.getting_started',
        items: [
          { path: '/docs/admin' },
          { path: '/docs/admin/initial-setup' },
        ],
      },
      {
        titleKey: 'admin_menu.ministry_management',
        items: [
          { path: '/docs/admin/ministry-management/schedule' },
          { path: '/docs/admin/ministry-management/currentneeds' },
          { path: '/docs/admin/ministry-management/experiences' },
          { path: '/docs/admin/ministry-management/day-overseers' },
          { path: '/docs/admin/ministry-management/shift-plan' },
          { path: '/docs/admin/ministry-management/events' },
          { path: '/docs/admin/ministry-management/shift-tags' },
          { path: '/docs/admin/ministry-management/locations' },
          { path: '/docs/admin/ministry-management/stores' },
          { path: '/docs/admin/ministry-management/publications' },
          { path: '/docs/admin/ministry-management/carts' },
          { path: '/docs/admin/ministry-management/timebands' },
        ],
      },
      {
        titleKey: 'user.publisher_management',
        items: [
          { path: '/docs/admin/publisher-management/publishers' },
          { path: '/docs/admin/publisher-management/congregations' },
          { path: '/docs/admin/publisher-management/circuits' },
          { path: '/docs/admin/publisher-management/teams' },
          { path: '/docs/admin/publisher-management/training' },
          { path: '/docs/admin/publisher-management/bulk-import' },
          { path: '/docs/admin/publisher-management/rating-labels' },
        ],
      },
      {
        titleKey: 'admin_menu.site_management',
        items: [
          { path: '/docs/admin/site-management/reports' },
          { path: '/docs/admin/site-management/news' },
          { path: '/docs/admin/site-management/alerts' },
          { path: '/docs/admin/site-management/faqs' },
          { path: '/docs/admin/site-management/videos' },
          { path: '/docs/admin/site-management/documents' },
          { path: '/docs/admin/site-management/taskcategories' },
          { path: '/docs/admin/site-management/contact-options' },
          { path: '/docs/admin/site-management/cancellation-reasons' },
          { path: '/docs/admin/site-management/tags' },
        ],
      },
      {
        titleKey: 'setting.title',
        items: [
          { path: '/docs/admin/settings/settings' },
          { path: '/docs/admin/settings/roles' },
          { path: '/docs/admin/settings/languages' },
          { path: '/docs/admin/settings/mcp-server' },
        ],
      },
      {
        titleKey: 'maintenance.title',
        items: [
          {
            path: '/docs/maintenance/updating',
            permissions: ['update_application'],
            title: { key: 'updates.title', count: 2 },
          },
          { path: '/docs/admin/maintenance/system-info' },
          { path: '/docs/admin/maintenance/system-monitor-dashboard' },
        ],
      },
    ],
  },
  {
    value: 'maintenance',
    title: { key: 'maintenance.title' },
    icon: 'fas fa-cog',
    sections: [
      {
        titleKey: 'help.getting_started',
        items: [
          { path: '/docs/maintenance' },
          { path: '/docs/maintenance/initial-setup' },
        ],
      },
      {
        titleKey: 'help.installation',
        items: [
          { path: '/docs/maintenance/obtain-domain' },
          { path: '/docs/maintenance/aws-rds-setup' },
          { path: '/docs/maintenance/local-database-setup' },
          { path: '/docs/maintenance/aws-ec2-setup' },
          { path: '/docs/maintenance/application-install' },
          { path: '/docs/maintenance/aws-s3-setup' },
          { path: '/docs/maintenance/outbound-email-setup' },
          { path: '/docs/maintenance/post-install-configuration' },
        ],
      },
      {
        titleKey: 'documentation.maintenance.other_hosting',
        items: [
          { path: '/docs/maintenance/aws-lightsail-setup' },
          { path: '/docs/maintenance/ionos-domain-setup' },
          { path: '/docs/maintenance/ionos-database-setup' },
          { path: '/docs/maintenance/ionos-application-install' },
        ],
      },
      {
        titleKey: 'setting.integration',
        items: [
          { path: '/docs/maintenance/google-maps-integration' },
          { path: '/docs/maintenance/openweathermap-integration' },
          { path: '/docs/maintenance/google-authentication-setup' },
          { path: '/docs/maintenance/google-contacts-integration' },
          { path: '/docs/maintenance/google-calendar-integration' },
          { path: '/docs/maintenance/google-drive-integration' },
        ],
      },
      {
        titleKey: 'documentation.maintenance.ongoing',
        items: [
          { path: '/docs/maintenance/updating', appendHash: true },
          { path: '/docs/maintenance/update-aws-rds' },
          { path: '/docs/maintenance/migrate-mysql-to-mariadb' },
        ],
      },
      {
        titleKey: 'documentation.maintenance.misc',
        items: [
          { path: '/docs/admin/maintenance/system-monitor-dashboard', appendHash: true },
          { path: '/docs/maintenance/background-tasks' },
        ],
      },
    ],
  },
];

/** Turns a title into text, translating it with `t` unless it is a product name. */
export function translateHelpTitle(title: HelpTitle, t: (key: string, plural?: number) => string): string {
  if ('text' in title) {
    return title.text;
  }
  return title.count === undefined
    ? t(title.key)
    : t(title.key, title.count);
}

/** The help page behind a route path, ignoring any `#anchor` or query string. */
export function findHelpPage(path: string): HelpPage | undefined {
  const bare = path.split(/[?#]/)[0];
  return helpPages.find(page => page.path === bare);
}
